import React, { useState } from 'react';
import styled, { ThemeProvider } from 'styled-components';

// ============ Themes (sync com restante do V2) ============
const lightTheme = {
  mode: 'light',
  bg: '#FAFAF7', surface: '#FFFFFF', surfaceAlt: '#F5F3EE',
  ink: '#1A1F2B', inkSoft: '#3D434F', muted: '#6B7280',
  line: '#E8E5DE', gold: '#B8935A', goldSoft: '#F2EBDD',
  ok: '#2F7D5B', danger: '#9A3B3B',
  shadow: 'rgba(0,0,0,.06)',
};
const darkTheme = {
  mode: 'dark',
  bg: '#0F1117', surface: '#181B23', surfaceAlt: '#1F2330',
  ink: '#F1ECE2', inkSoft: '#C9C4B8', muted: '#8A8F9A',
  line: '#262A36', gold: '#C9A06A', goldSoft: 'rgba(201,160,106,.14)',
  ok: '#5BB089', danger: '#C5544A',
  shadow: 'rgba(0,0,0,.4)',
};
const THEME_KEY = 'mf_admin_v2_theme';
const VIDEO_RE = /\.(mp4|mov|webm|avi|mkv|m4v)(\?|$)/i;
const isVideo = (u) => VIDEO_RE.test(u || '');

// ============ Styled ============
const Shell = styled.div`
  display: grid;
  grid-template-columns: 240px 1fr;
  min-height: 100vh;
  background: ${p => p.theme.bg};
  color: ${p => p.theme.ink};
  font-family: 'Inter', -apple-system, sans-serif;
  @media (max-width: 900px) { grid-template-columns: 1fr; }
`;
const Sidebar = styled.aside`
  background: ${p => p.theme.surface};
  border-right: 1px solid ${p => p.theme.line};
  padding: 32px 20px;
  display: flex; flex-direction: column; gap: 4px;
  position: sticky; top: 0; align-self: start;
  height: 100vh; overflow-y: auto;
  @media (max-width: 900px) {
    position: static; height: auto;
    flex-direction: row; flex-wrap: wrap;
    padding: 14px 20px; gap: 6px; align-items: center;
    border-right: none; border-bottom: 1px solid ${p => p.theme.line};
  }
`;
const Brand = styled.div`
  padding: 0 8px 24px;
  border-bottom: 1px solid ${p => p.theme.line};
  margin-bottom: 20px;
  display: flex; flex-direction: column; align-items: center; gap: 8px;
  img { height: 56px; width: auto; max-width: 180px; object-fit: contain; display: block; }
  span.tagline {
    font-family: 'Inter', sans-serif;
    font-size: 10px; letter-spacing: 3px;
    color: ${p => p.theme.gold};
    text-transform: uppercase; font-weight: 500;
  }
  @media (max-width: 900px) {
    border-bottom: none; padding: 0; margin: 0 auto 0 0;
    flex-direction: row; gap: 12px;
    img { height: 36px; }
    span.tagline { display: none; }
  }
`;
const NavGroup = styled.div`
  font-size: 10px; letter-spacing: 2.5px;
  color: ${p => p.theme.muted}; text-transform: uppercase;
  padding: 16px 8px 8px; font-weight: 500;
  @media (max-width: 900px) { display: none; }
`;
const NavItem = styled.button`
  display: flex; align-items: center; gap: 12px;
  width: 100%; border: none; text-align: left;
  background: ${p => p.$active ? p.theme.goldSoft : 'transparent'};
  color: ${p => p.$active ? p.theme.ink : p.theme.inkSoft};
  padding: 11px 14px; border-radius: 8px;
  font-size: 14px; font-weight: ${p => p.$active ? 600 : 400};
  cursor: pointer; letter-spacing: 0.2px; font-family: inherit;
  &:hover { background: ${p => p.$active ? p.theme.goldSoft : p.theme.surfaceAlt}; }
  &:disabled { color: ${p => p.theme.muted}; cursor: not-allowed; opacity: .6; }
  small {
    margin-left: auto; font-size: 9px; letter-spacing: 1.5px;
    color: ${p => p.theme.gold}; text-transform: uppercase;
  }
`;
const Main = styled.main`
  display: flex; flex-direction: column; min-width: 0;
`;
const Topbar = styled.header`
  display: flex; align-items: center; justify-content: space-between;
  padding: 22px 40px;
  border-bottom: 1px solid ${p => p.theme.line};
  background: ${p => p.theme.surface};
  flex-wrap: wrap; gap: 12px;
  @media (max-width: 600px) { padding: 14px 20px; }
`;
const Crumb = styled.div`
  font-size: 12px; letter-spacing: 1.5px;
  color: ${p => p.theme.muted}; text-transform: uppercase;
  span { color: ${p => p.theme.ink}; font-weight: 500; }
`;
const TopActions = styled.div`
  display: flex; gap: 8px; align-items: center; flex-wrap: wrap;
`;
const TopBtn = styled.button`
  border: 1px solid ${p => p.theme.line};
  background: ${p => p.$primary ? p.theme.ink : p.theme.surface};
  color: ${p => p.$primary ? p.theme.bg : p.theme.ink};
  padding: 9px 18px; border-radius: 6px;
  font-size: 13px; font-weight: 500; letter-spacing: 0.3px;
  cursor: pointer; font-family: inherit; transition: all .15s;
  &:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 2px 8px ${p => p.theme.shadow}; }
  &:disabled { opacity: .5; cursor: not-allowed; }
`;
const ThemeToggle = styled.button`
  border: 1px solid ${p => p.theme.line};
  background: ${p => p.theme.surface};
  color: ${p => p.theme.ink};
  width: 38px; height: 38px; border-radius: 50%;
  cursor: pointer; font-size: 14px;
  display: flex; align-items: center; justify-content: center;
`;
const Saved = styled.span`
  font-size: 12px; color: ${p => p.theme.ok}; letter-spacing: 0.5px;
`;
const Content = styled.div`
  padding: 40px 48px;
  max-width: 980px;
  width: 100%;
  margin: 0 auto;
  @media (max-width: 600px) { padding: 24px 16px; }
`;

const PageHead = styled.div`
  margin-bottom: 28px;
  h1 {
    font-family: 'Cormorant Garamond', Georgia, serif;
    font-size: 38px; font-weight: 500;
    color: ${p => p.theme.ink};
    margin: 0 0 4px; letter-spacing: -0.3px;
  }
  p { color: ${p => p.theme.muted}; font-size: 14px; }
`;

const Section = styled.section`
  background: ${p => p.theme.surface};
  border: 1px solid ${p => p.theme.line};
  border-radius: 10px;
  padding: 24px 28px;
  margin-bottom: 18px;
  @media (max-width: 600px) { padding: 18px 16px; }
`;
const SecTitle = styled.h2`
  font-family: 'Cormorant Garamond', Georgia, serif;
  font-size: 22px; font-weight: 600;
  color: ${p => p.theme.ink};
  margin: 0 0 4px;
  &::after {
    content: ''; display: block; width: 30px; height: 2px;
    background: ${p => p.theme.gold}; margin-top: 6px;
  }
`;
const SecHint = styled.p`
  font-size: 12px; color: ${p => p.theme.muted};
  margin: 0 0 18px; letter-spacing: 0.2px; line-height: 1.5;
`;
const Field = styled.label`
  display: flex; flex-direction: column; gap: 6px;
  font-size: 10px; letter-spacing: 1.5px;
  text-transform: uppercase; color: ${p => p.theme.muted}; font-weight: 500;
  input {
    background: ${p => p.theme.bg};
    border: 1px solid ${p => p.theme.line};
    color: ${p => p.theme.ink};
    border-radius: 7px; padding: 10px 12px;
    font-size: 14px; font-family: inherit;
    letter-spacing: 0.2px; text-transform: none; font-weight: 400;
    outline: none; transition: border-color .15s;
    &:focus { border-color: ${p => p.theme.gold}; box-shadow: 0 0 0 3px ${p => p.theme.goldSoft}; }
  }
`;

// Hero upload
const HeroRow = styled.div`
  display: flex; gap: 18px; align-items: flex-start; flex-wrap: wrap;
`;
const HeroPreview = styled.div`
  width: 320px; height: 180px;
  border-radius: 10px;
  background: ${p => p.theme.surfaceAlt};
  border: 1px solid ${p => p.theme.line};
  overflow: hidden;
  position: relative;
  display: flex; align-items: center; justify-content: center;
  color: ${p => p.theme.muted};
  font-size: 11px; letter-spacing: 1.5px; text-transform: uppercase;
  img, video { width: 100%; height: 100%; object-fit: cover; display: block; }
`;
const HeroActions = styled.div`
  display: flex; flex-direction: column; gap: 8px;
  flex: 1; min-width: 220px;
  .file {
    position: relative;
    display: inline-flex; align-items: center; justify-content: center;
    background: ${p => p.theme.ink};
    color: ${p => p.theme.bg};
    padding: 11px 18px; border-radius: 7px;
    font-size: 13px; font-weight: 500; letter-spacing: 0.5px;
    cursor: pointer;
    input { position: absolute; inset: 0; opacity: 0; cursor: pointer; }
  }
  .remove {
    background: transparent; border: 1px solid ${p => p.theme.line};
    color: ${p => p.theme.danger};
    padding: 9px 16px; border-radius: 7px;
    font-size: 13px; cursor: pointer; font-family: inherit;
    align-self: flex-start;
    &:hover { border-color: ${p => p.theme.danger}; }
  }
  .hint {
    font-size: 11px; color: ${p => p.theme.muted}; line-height: 1.5;
    text-transform: none; letter-spacing: 0; font-weight: 400;
  }
`;

// Chips
const ChipList = styled.div`
  display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 12px;
`;
const Chip = styled.span`
  display: inline-flex; align-items: center; gap: 8px;
  background: ${p => p.theme.goldSoft};
  color: ${p => p.theme.ink};
  padding: 6px 12px; border-radius: 999px;
  font-size: 13px; font-family: 'SF Mono', monospace;
  border: 1px solid ${p => p.theme.gold};
  button {
    border: none; background: transparent;
    color: ${p => p.theme.inkSoft};
    cursor: pointer; font-size: 16px; line-height: 1; padding: 0;
    &:hover { color: ${p => p.theme.danger}; }
  }
`;
const ChipEmpty = styled.div`
  color: ${p => p.theme.muted};
  font-size: 12px; font-style: italic;
  margin-bottom: 12px;
`;
const InlineRow = styled.div`
  display: flex; gap: 8px;
  input {
    flex: 1;
    background: ${p => p.theme.bg};
    border: 1px solid ${p => p.theme.line};
    color: ${p => p.theme.ink};
    border-radius: 7px; padding: 10px 12px;
    font-size: 14px; font-family: 'SF Mono', monospace;
    outline: none;
    &:focus { border-color: ${p => p.theme.gold}; }
  }
  button {
    border: 1px solid ${p => p.theme.gold};
    background: ${p => p.theme.gold};
    color: #fff;
    padding: 0 18px; border-radius: 7px;
    cursor: pointer; font-size: 13px; font-weight: 500;
    font-family: inherit;
    &:hover { transform: translateY(-1px); }
  }
`;

// ============ Component ============
export default function ConfigPageV2({
  admin,
  config, setConfig,
  saveConfig, configLoading, configSaved,
  handleHeroUpload, removeHeroImage, heroUploading,
  novoNumero, setNovoNumero, addNumero, removeNumero,
  novoNumeroNotif, setNovoNumeroNotif, addNumeroNotif, removeNumeroNotif,
  onLogout,
  onNavDashboard, onNavImoveis, onNavAmenidades, onNavCorretores, onNavBlog, onNavDisparos,
  goClassic,
}) {
  const isDev = !!admin?.is_dev;
  const canSeeDisparos = isDev || admin?.role === 'admin';
  const [mode, setMode] = useState(() => {
    if (typeof window === 'undefined') return 'light';
    return localStorage.getItem(THEME_KEY) === 'dark' ? 'dark' : 'light';
  });
  const theme = mode === 'dark' ? darkTheme : lightTheme;
  const toggleMode = () => {
    const next = mode === 'dark' ? 'light' : 'dark';
    setMode(next);
    localStorage.setItem(THEME_KEY, next);
  };

  const heroIsVideo = config.hero_image_url && isVideo(config.hero_image_url);

  return (
    <ThemeProvider theme={theme}>
      <Shell>
        <Sidebar>
          <Brand>
            <img
              src={mode === 'dark' ? '/mf-real-state/logos/mf-logo-dark.png' : '/mf-real-state/logos/mf-logo-light.png'}
              alt="MF Imóveis"
            />
            <span className="tagline">real estate</span>
          </Brand>

          <NavGroup>Painel</NavGroup>
          <NavItem onClick={onNavDashboard}>Visão geral</NavItem>
          <NavItem onClick={onNavImoveis}>Imóveis</NavItem>
          <NavItem onClick={onNavAmenidades} style={{ paddingLeft: 28, fontSize: 13 }}>↳ Amenidades</NavItem>
          <NavItem onClick={onNavCorretores}>Corretores</NavItem>
          <NavItem disabled>Leads <small>em breve</small></NavItem>
          <NavItem disabled>Visitas <small>em breve</small></NavItem>
          <NavItem onClick={onNavBlog}>Blog</NavItem>

          {canSeeDisparos && onNavDisparos && (<>
            <NavGroup>Marketing</NavGroup>
            <NavItem onClick={onNavDisparos}>
              Disparos
            </NavItem>
          </>)}

          <NavGroup>Sistema</NavGroup>
          <NavItem $active>Configurações</NavItem>
          <NavItem onClick={onLogout}>Sair</NavItem>
        </Sidebar>

        <Main>
          <Topbar>
            <Crumb>Painel · <span>Configurações</span></Crumb>
            <TopActions>
              {configSaved && <Saved>Salvo!</Saved>}
              <ThemeToggle onClick={toggleMode} title={mode === 'dark' ? 'Modo claro' : 'Modo escuro'}>
                {mode === 'dark' ? '☀' : '☾'}
              </ThemeToggle>
              <TopBtn $primary onClick={saveConfig} disabled={configLoading}>
                {configLoading ? 'Salvando…' : 'Salvar'}
              </TopBtn>
            </TopActions>
          </Topbar>

          <Content>
            <PageHead>
              <h1>Configurações</h1>
              <p>Ajustes gerais do site e do agente WhatsApp.</p>
            </PageHead>

            {/* Capa da home */}
            <Section>
              <SecTitle>Capa da home</SecTitle>
              <SecHint>Imagem ou vídeo de fundo do topo da página inicial. Se nada for definido, usa a padrão.</SecHint>
              <HeroRow>
                <HeroPreview>
                  {config.hero_image_url ? (
                    heroIsVideo ? (
                      <video src={config.hero_image_url} autoPlay muted loop playsInline />
                    ) : (
                      <img src={config.hero_image_url} alt="Capa atual" />
                    )
                  ) : (
                    'Usando padrão'
                  )}
                </HeroPreview>
                <HeroActions>
                  <label className="file">
                    {heroUploading ? 'Enviando…' : config.hero_image_url ? 'Trocar mídia' : 'Enviar imagem ou vídeo'}
                    <input
                      type="file"
                      accept="image/*,video/mp4,video/webm,video/quicktime"
                      onChange={handleHeroUpload}
                      disabled={heroUploading}
                    />
                  </label>
                  {config.hero_image_url && (
                    <button className="remove" onClick={removeHeroImage} disabled={heroUploading}>
                      Remover
                    </button>
                  )}
                  <span className="hint">
                    Aceita imagem ou vídeo (mp4/webm). Vídeos menores que 10MB.<br />
                    Não esqueça de clicar em <b>Salvar</b> no topo após trocar.
                  </span>
                </HeroActions>
              </HeroRow>
            </Section>

            {/* WhatsApp uazapi */}
            <Section>
              <SecTitle>WhatsApp (uazapi)</SecTitle>
              <SecHint>Token da instância uazapi conectada à conta de WhatsApp do agente.</SecHint>
              <Field>
                Token da instância
                <input
                  value={config.uazapi_token || ''}
                  onChange={e => setConfig(c => ({ ...c, uazapi_token: e.target.value }))}
                  placeholder="Ex: 1a181e60-97d5-4361-9626-..."
                  style={{ fontFamily: 'SF Mono, monospace', fontSize: 13 }}
                />
              </Field>
            </Section>

            {/* Números autorizados */}
            <Section>
              <SecTitle>Números autorizados <span style={{ color: theme.muted, fontSize: 14, fontFamily: 'Inter', fontWeight: 400 }}>({config.numeros_autorizados.length})</span></SecTitle>
              <SecHint>Apenas esses números podem cadastrar imóveis pelo WhatsApp. Formato: país + DDD + número (ex: 5511999999999).</SecHint>
              <ChipList>
                {config.numeros_autorizados.map((num, i) => (
                  <Chip key={i}>
                    {num}
                    <button onClick={() => removeNumero(i)} title="Remover">×</button>
                  </Chip>
                ))}
              </ChipList>
              {config.numeros_autorizados.length === 0 && <ChipEmpty>Nenhum número adicionado</ChipEmpty>}
              <InlineRow>
                <input
                  value={novoNumero}
                  onChange={e => setNovoNumero(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addNumero())}
                  placeholder="5511999999999"
                />
                <button onClick={addNumero}>Adicionar</button>
              </InlineRow>
            </Section>

            {/* Notificação de leads */}
            <Section>
              <SecTitle>Notificação de leads <span style={{ color: theme.muted, fontSize: 14, fontFamily: 'Inter', fontWeight: 400 }}>({config.numeros_notificacao_leads.length})</span></SecTitle>
              <SecHint>Esses números recebem mensagem no WhatsApp quando alguém preenche o formulário de contato no site.</SecHint>
              <ChipList>
                {config.numeros_notificacao_leads.map((num, i) => (
                  <Chip key={i}>
                    {num}
                    <button onClick={() => removeNumeroNotif(i)} title="Remover">×</button>
                  </Chip>
                ))}
              </ChipList>
              {config.numeros_notificacao_leads.length === 0 && <ChipEmpty>Nenhum número adicionado</ChipEmpty>}
              <InlineRow>
                <input
                  value={novoNumeroNotif}
                  onChange={e => setNovoNumeroNotif(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addNumeroNotif())}
                  placeholder="5511999999999"
                />
                <button onClick={addNumeroNotif}>Adicionar</button>
              </InlineRow>
            </Section>

            {/* Encaminhamento de leads */}
            <Section>
              <SecTitle>Encaminhamento de leads</SecTitle>
              <SecHint>
                Telefone único que recebe os contatos dos botões "Agendar visita" e "Quero mais informações" nas páginas dos imóveis.
              </SecHint>
              <Field>
                Telefone (formato 5511999999999)
                <input
                  value={config.telefone_encaminhamento_leads || ''}
                  onChange={e => setConfig(c => ({ ...c, telefone_encaminhamento_leads: e.target.value.replace(/\D/g, '') }))}
                  placeholder="5511999999999"
                  style={{ fontFamily: 'SF Mono, monospace', fontSize: 13 }}
                />
              </Field>
            </Section>

            {/* Portal de Parceiros (feed XML) */}
            <Section>
              <SecTitle>Portal de Parceiros</SecTitle>
              <SecHint>
                URL do feed XML que portais imobiliários (OLX Pro, ZAP/VivaReal, ImovelWeb, etc.) consomem pra listar os imóveis MF.
                Cadastre essa URL no painel de cada portal — eles puxam automaticamente a cada 3–12 horas.
                Apenas imóveis com o toggle <b style={{ color: theme.gold }}>"Publicar nos portais parceiros"</b> ativo no cadastro aparecem aqui.
              </SecHint>

              <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 18 }}>
                <code style={{
                  flex: 1, minWidth: 280,
                  fontFamily: 'SF Mono, monospace',
                  background: theme.bg,
                  border: `1px solid ${theme.line}`,
                  borderRadius: 7,
                  padding: '10px 14px',
                  fontSize: 13,
                  color: theme.gold,
                  overflowX: 'auto',
                  whiteSpace: 'nowrap',
                }}>
                  https://www.mfrealstate.com.br/api/feed
                </code>
                <button
                  type="button"
                  onClick={() => {
                    navigator.clipboard.writeText('https://www.mfrealstate.com.br/api/feed');
                  }}
                  style={{
                    border: `1px solid ${theme.gold}`,
                    background: theme.gold,
                    color: '#fff',
                    padding: '10px 18px',
                    borderRadius: 7,
                    fontSize: 13,
                    fontWeight: 500,
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                  }}
                >
                  📋 Copiar URL
                </button>
                <a
                  href="https://www.mfrealstate.com.br/api/feed"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    border: `1px solid ${theme.line}`,
                    background: 'transparent',
                    color: theme.ink,
                    padding: '10px 18px',
                    borderRadius: 7,
                    fontSize: 13,
                    fontWeight: 500,
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                    textDecoration: 'none',
                  }}
                >
                  Ver feed ↗
                </a>
              </div>

              <div style={{
                background: theme.bg,
                border: `1px solid ${theme.line}`,
                borderRadius: 8,
                padding: '14px 18px',
                fontSize: 12,
                color: theme.inkSoft,
                lineHeight: 1.6,
              }}>
                <div style={{ fontSize: 10, letterSpacing: 1.5, color: theme.gold, textTransform: 'uppercase', fontWeight: 600, marginBottom: 6 }}>
                  Como ativar em cada portal
                </div>
                <div style={{ marginBottom: 8 }}>
                  <b style={{ color: theme.ink }}>OLX Pro / ZAP / VivaReal:</b> Painel anunciante → Integrações → Cadastrar XML → cola a URL acima
                </div>
                <div style={{ marginBottom: 8 }}>
                  <b style={{ color: theme.ink }}>ImovelWeb:</b> Suporte → Solicitar integração XML → enviar a URL
                </div>
                <div>
                  <b style={{ color: theme.ink }}>Outros portais:</b> Procurar "feed XML" ou "Universal Real Estate format" no painel deles. O formato é o padrão de mercado.
                </div>
              </div>
            </Section>
          </Content>
        </Main>
      </Shell>
    </ThemeProvider>
  );
}
